import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { getAdminChatModelCatalog } from "../../api/message";
import { effectiveAccessTier } from "../../utils/user";

// Both chat surfaces use the same account-owned settings. No browser-global
// model preference can leak to another signed-in account.
export default function AdminExperienceControl({ userData, setUserData, compact = false, open: controlledOpen, onOpenChange, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [catalog, setCatalog] = useState(null);
  const [tier, setTier] = useState("");
  const [model, setModel] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isAdmin = userData?.is_admin_user === true;
  useEffect(() => {
    if (!open || !isAdmin) return undefined;
    let active = true;
    setError("");
    setTier(userData.admin_test_tier || "");
    setModel(userData.admin_response_model || "");
    setReasoning(userData.admin_response_reasoning_effort || "");
    getAdminChatModelCatalog().then((value) => {
      if (active) setCatalog(value);
    }).catch(() => {
      if (active) setError("Model catalog unavailable. Close and reopen to retry.");
    });
    return () => { active = false; };
  }, [open, isAdmin, userData?.user_id]); // Settings are copied when opening, not while editing.

  if (!isAdmin) return null;
  const models = catalog?.models || [];
  const selected = models.find((row) => row.model_name === (model || catalog?.default_model_name));
  const efforts = selected?.reasoning_efforts || [];
  const label = `Testing ${effectiveAccessTier(userData).toUpperCase()} · Main response: ${userData.admin_response_model || "Default"}`;
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: "mutation($tier: String, $model: String, $reasoning: String) { updateAdminExperience(tier: $tier, model: $model, reasoningEffort: $reasoning) }",
          variables: { tier: tier || null, model: model || null, reasoning: reasoning || null },
        }),
      });
      const json = await response.json();
      const saved = json.data?.updateAdminExperience;
      if (!response.ok || json.errors?.length || !saved) throw new Error(json.errors?.[0]?.message || "Save failed");
      setUserData((current) => current?.user_id === saved.user_id ? { ...current, ...saved } : current);
      setOpen(false);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  return <>
    {!hideTrigger && <Button data-admin-experience-control size="small" onClick={() => setOpen(true)} title={label}
      sx={{ fontSize: 11, maxWidth: compact ? { xs: 150, sm: 310 } : "100%", lineHeight: 1.25, textTransform: "none" }}>
      {label}
    </Button>}
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="sm">
      <DialogTitle>Admin experience</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Actual subscription: {userData.has_active_subscription ? (userData.billing_subscription_plan_tier || "Pro").toUpperCase() : "Free"}
          {userData.max_trial_active ? " · Max trial active" : ""}. Test selections apply to new questions and preserve billing and usage.
        </Typography>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField select label="Account experience" value={tier} onChange={(e) => setTier(e.target.value)} disabled={busy}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Use actual subscription</MenuItem>
            {["free", "pro", "max"].map((value) => <MenuItem key={value} value={value}>{value.toUpperCase()}</MenuItem>)}
          </TextField>
          <TextField select label="Main response model" value={model} disabled={busy || !catalog}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
            onChange={(e) => { setModel(e.target.value); setReasoning(""); }}>
            <MenuItem value="">Default</MenuItem>
            {models.map((row) => <MenuItem key={row.model_name} value={row.model_name}>{row.model_name}</MenuItem>)}
          </TextField>
          <TextField select label="Test reasoning effort" value={reasoning} disabled={busy || !catalog} onChange={(e) => setReasoning(e.target.value)}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Use tier default</MenuItem>
            {efforts.map((entry) => {
              const value = typeof entry === "string" ? entry : entry.value;
              return <MenuItem key={value} value={value}>{value}</MenuItem>;
            })}
          </TextField>
          <Typography variant="caption">{selected?.token_costs?.label || "Pricing unavailable"}. Catalog availability does not verify provider access. Customer model defaults are unchanged.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button data-admin-experience-save disabled={busy} onClick={save} variant="contained">{busy ? "Saving…" : "Save experience"}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
